import React, { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProjectEvents } from '../../helpers/projectEvents';
import { createCopyLinkProjectFn } from '../../helpers/copyLink';
import { useToasts, DropdownListItem } from '@brg/ui';
import { CreateLinkTo, LinkTo } from '../../constants/linkTo';
import styled from 'styled-components';
import { useHistory, useRouteMatch } from "react-router-dom";
import { API, Designer, DesignerAPI, Endpoints, Project } from '@brg/api';
import { bProfile, ProfileRole, useProfile } from "@brg/auth";
import { ProjectDelegate } from "../../../project-delegate";
import { AppTester } from "../../../supporting";
import { protectProcessStatuses } from 'shared/helpers/protectStatus';
import { css } from 'styled-components';
import { HsePolicies } from 'shared/constants/HsePolicies';
import {ModerationType} from "../../pages/teams/const/ModerationTypes";
import { FoldersDropdown, ORIENTATION_TYPE } from '../FoldersDropdown/FoldersDropdown';
import { downloadPDF } from '../PrintProject/model/downloadPDF';
import { ProjectStatusDrafts } from 'shared/helpers/projectStatus';
import { Placement } from './types';
import { RequestPublicationModal } from 'shared/modals/RequestPublicationModal';
import { createPortal } from 'react-dom';

export interface ProjectTooltipProps {
  isViewable?: boolean;
  isPublishable?: boolean;
  isDeletable?: boolean;
  isEditable?: boolean;
  isRemovableFromShowcase?: boolean;
  isRemovableFromBook?: boolean;
  isCanBeDraft?: boolean;
  isCanBeRepost?: boolean;
  isModerately?: boolean;
  identifier?: string;
  isReportable?: boolean;
  isPartInCompetition?: boolean;
  isHiddenCancel?: boolean;
  isCopyLink?: boolean;
  isCompetitionModer?: boolean;
  isInOnDisplay?: boolean;
  projectType?: string;
  isSuperModer?: boolean;
  isTeamOwner?: boolean
  isLongreadAndTeamOwner?: boolean
  isTagModerator?: boolean;
  isTagExpert?: boolean;
  orientationType?: ORIENTATION_TYPE,
  isBookmarked?: boolean;
  isCoauthor?: boolean;
  canMakeBilingual?: boolean;
  isPresentation?: boolean;
  canModerSendToCompetition?: boolean;
  canAddBookmark?: boolean;
  competitionList?: Record<string, string[]>[];
  moderId?: string;
  approvalInfo?: {context: string, name: string};
  project?: Project;
  linkPrefix?: string;
  isCanNotParticipate?: boolean;
  isCanVisibleCompetitionList?: boolean;
  canSendGift?: boolean;
  shouldVisibleProtectButton?: boolean;
  isNominated?: boolean;
  canViewStatistic?: boolean;
  canEditFolders?: boolean;
  isProjectAITranslateRemovable?: boolean;
  toggleTooltip?: (v: any) => void;
  placement?: Placement;
  repostTools?: any;
  ref?: any
}

export interface MobileTooltipProps {
  tooltipOpen: boolean,
  toggleTooltip: (value: boolean) => void
}


export const StyledDropdownListItem = styled(DropdownListItem)<{isProfile?: boolean, isContinuumSite?: boolean}>`
position: relative;
// z-index: 1;
 ${({ isProfile }) => isProfile && css`
    background: #e7e7e7;

      &:hover {
        background: #F2F2F2;
      }`
}
 ${({ isContinuumSite }) => isContinuumSite && css`
    background: rgb(31, 31, 31);

      &:hover {
        background: rgb(44, 44, 43);
        color: #909090;
      }`
}
  
`;

export const displayProjectEditError = (project: any, toasts: any, t: any, lang: any) => {
  try {
    if (project) {
      const { contexts } = project;
      const hasContexts = !!contexts?.filter((i:string) => i !== 'places' && i !== 'hsedesigntemp')?.length; 
      const contextMap = (project.approvals || []).reduce((result: any, {
        contextService,
        isApproved,
        accountId,
      }: {
        contextService: string,
        isApproved: boolean,
        accountId: string
      }) => {
        return {
          ...result,
          [contextService]: {
            contextService,
            isApproved,
            accountId,
          }
        }
      }, {});
      const approvalsArray = Object.values(contextMap);
      //@ts-ignore
      const [blockingApproval] = approvalsArray.filter(({
        isApproved,
        contextService
      }) => isApproved && contextService !== 'places' && contextService !== 'hsedesigntemp');
      if (blockingApproval) {
        const {
          //@ts-ignore
          accountId,
          //@ts-ignore
          contextService
        } = blockingApproval;
        //@ts-ignore
        DesignerAPI.Get({
          //@ts-ignore
          idDesigner: accountId
        })
        .then((d) => d.json())
        .then((designer) => {
          return API.toLang<Designer>(designer, lang || 'en')
        })
          .then(({ name, surName }) => {
            const approvalInfo = ({
              name: `${name} ${surName}`,
              context: contextService
            });
            toasts.error(t('toasts.project_on_display', approvalInfo));
            return;
          }).catch((e) => { throw e });
      }
      else if (hasContexts) {
        toasts.error(t('toasts.project_on_display_empty'));
        return;
      }
    }
  } catch (e) {
    toasts.error(t('toasts.project_on_display', {}));
  }
}

/**
 * **ProjectTooltip** - контент всплывающего меню со всеми необходимыми параметрами и настройками для проекта.
 *
 * @param props
 */
export const ProjectTooltip: FC<React.PropsWithChildren<ProjectTooltipProps & ProjectEvents>> = (props) => {
  const { t, i18n } = useTranslation();
  const toasts = useToasts();
  const history = useHistory();
  const profile = useProfile();
  const { error } = useToasts();
  const [showRequestPublicationModal, setShowRequestPublicationModal] = useState(false);

  const {
    isViewable,
    isPublishable,
    isEditable,
    isReportable,
    isDeletable,
    isCanBeDraft,
    repostTools,
    projectType,
    // isHiddenCancel,
    isCopyLink,
    identifier,
    isModerately,
    isRemovableFromShowcase,
    isRemovableFromBook,
    isCompetitionModer,
    isTeamOwner,
    isLongreadAndTeamOwner,
    canModerSendToCompetition,
    canAddBookmark,
    competitionList,
    isInOnDisplay,
    isPartInCompetition,
    children,
    isCanNotParticipate,
    canEditFolders,
    isPresentation,
    canSendGift,
    toggleTooltip,
    onNotParticipate,
    onAddBookmark,
    onRepostRemove,
    onMoveFromShowcase,
    onRemoveFromBook,
    onPublish,
    onDelete,
    onEdit,
    onMoveDrafts,
    onRejectCoauthor,
    onSendToCompetition,
    onMoveModerate,
    onReport,
    onView,
    onApprove,
    onReject,
    onProtect,
    onMakeImageForOrganization,
    onAttachProducts,
    isTagModerator,
    orientationType,
    isCoauthor,
    isSuperModer,
    project,
    linkPrefix,
    isBookmarked,
    shouldVisibleProtectButton,
    onShowOriginals,
    onDeleteAITranslation,
    canViewStatistic
  } = props;

  // Некоторые обработчики по умолчанию
  const isPro = AppTester.isProDeziiign();
  const isContinuumSite = AppTester.isContinuumSite();
  const isProfile = !!useRouteMatch(LinkTo.designer);
  const isHSEDesign = AppTester.isHSEDesign();
  const isDeziiign = AppTester.isDeziiign();
  // @ts-ignore
  const isLMS = Boolean(project?.teams?.find((team: any) => team?.moderationType === ModerationType.LMS)?.teamId || '')
      || project?.contexts?.some(( item: string ) => ['lmsproject', 'lmshseproject'].includes(item));

  const isOwner = useMemo(() => profile?.accountId === project?.author?.accountId, [profile?.accountId, project?.author?.accountId])

  const isModer = bProfile.hasRole(profile, ProfileRole.moder);
    //@ts-ignore
  const hasRootTeam =  !!project?.rootTeamId

  const canPrintPDF = hasRootTeam && (isOwner || isModer)

  const repost = useMemo(() => (repostTools) , [repostTools])
  
  const canEditAllPrj = useMemo(
    () => (profile && profile.rolesList && Array.isArray(profile.rolesList) && 
      ((new ProfileRole(HsePolicies.edit_project)).containsIn(profile.rolesList) ||
      isLMS && (new ProfileRole(HsePolicies.lms_admin)).containsIn(profile.rolesList))
  ), [isLMS, profile]);

  const isProjectAITranslateRemovable = useMemo(() => {
    //@ts-ignore
    return project?.autoTranslateStatus === 2 || project?.autoTranslateStatus === 3
  }, [project])

  // Функция для отправки запроса на публикацию
  const handleRequestPublication = useCallback(() => {
    if (!project?.projectId) return;
    
    console.log('Отправка запроса на публикацию для проекта:', project.projectId);
    // Модалка сама отправит API запрос и покажет успешное состояние
  }, [project?.projectId]);

  // Функция для открытия модалки запроса на публикацию
  const handleOpenRequestPublicationModal = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    if (!project?.projectId) {
      return;
    }
    setShowRequestPublicationModal(true);
  }, [project?.projectId]);

  const editFn = onEdit ?? (() => {
    if (!isSuperModer && !isTagModerator && !canEditAllPrj) {

      if (isInOnDisplay && !ProjectDelegate.canEditShowcaseProjects(profile)) {
        displayProjectEditError(project, toasts, t, i18n.language);
        return;
      }
      if (isPartInCompetition) {
        toasts.error(t('toasts.project_part_competition'));
        return;
      }
    }

    //@ts-ignore
    if (isPresentation || (['fixed-ratio-large', 'fixed-ratio-small', 't-shape']?.includes(project?.projectType) && !isPro && !isLMS)) {
      if (isHSEDesign) {
        const hseProUrl = window.location.origin.includes("com") 
          ? process.env.REACT_APP_URL_HSEDESIGN_PRO_COM 
          : process.env.REACT_APP_URL_HSEDESIGN_PRO_RU;
        const baseUrl = hseProUrl?.endsWith('/') ? hseProUrl.slice(0, -1) : hseProUrl;
        window.open(`${baseUrl}/edit/${identifier}`)
      }
      else if (isDeziiign && !isHSEDesign) {
        const baseUrl = process.env.REACT_APP_URL_DEZIIIGN_PRO?.endsWith('/') 
          ? process.env.REACT_APP_URL_DEZIIIGN_PRO.slice(0, -1) 
          : process.env.REACT_APP_URL_DEZIIIGN_PRO;
        window.open(`${baseUrl}/edit/${identifier}`)
      }
      else {
        const baseUrl = process.env.REACT_APP_URL_DEZIIIGN_PRO?.endsWith('/') 
          ? process.env.REACT_APP_URL_DEZIIIGN_PRO.slice(0, -1) 
          : process.env.REACT_APP_URL_DEZIIIGN_PRO;
        window.open(`${baseUrl}/edit/${identifier}`)
      }
      return;
    }
    if (project?.isBook) {
      history.push(((linkPrefix || '') + CreateLinkTo.bookEdit(identifier ?? 'undefined')));
    } else if (isLMS && ProjectStatusDrafts(project)) {
      window.open(process.env.REACT_APP_URL_HSEDESIGN_LMS_COM + CreateLinkTo.projectEdit(identifier ?? 'undefined'))
    } else {
      history.push(((linkPrefix || '') + CreateLinkTo.projectEdit(identifier ?? 'undefined')));
    }
  });

  const protextButtonText = useMemo(() => {
    const isProcess = (project as any)?.protectedStatus as number === protectProcessStatuses.InProcess;
    const isNotStart = (project as any)?.protectedStatus as number === protectProcessStatuses.NotStarted;

    if (isProcess) {
      return t('inProgress');
    }

    return !(project as any)?.isProtected || isNotStart
      ? t('protect')
      : t('unprotect');
  }, [project, t])

  const printPDFForm = useMemo(() => ({
    orientation: true,
    size: 'A4',
    localization: i18n.language,
    projectId: project?.projectId,
    pathUrlProjectRequest: 'testPp',
    pathUrlLongreadRequest: 'testLn',
    // @ts-ignore
    isPresentation: ['fixed-ratio-large', 'fixed-ratio-small', 't-shape']?.includes(project?.projectType),
}), [project, i18n.language]);


const printPdfCard = useCallback(() => {

    toasts.info(t('print_project_toast'))

    API.request()
        .endpoint(Endpoints.geograffee)
        .url('/api/Project/PrintPdf')
        .post()
        .body(printPDFForm)
        .send()
        .then((r) => r.json())
        // @ts-ignore
        .then((r) => r?.length > 0 ? downloadPDF(r, project?.title, error, toasts.info, t) : console.error('no files'))
        .catch((e) => console.error(e))
}, [printPDFForm, t]);


  const protectInprocess = useMemo(
    () => (project as any)?.protectedStatus as number === protectProcessStatuses.InProcess, [project])

  const fireOpen = (ev: React.MouseEvent) => {
    ev.preventDefault();
    ev.stopPropagation()
    window.open((Endpoints.geograffee + "/api/Project/GetStatisticForProjest?projectId=" + identifier), '_blank')?.focus();
  };

  const fireEvent = (handler?: (s: string) => void) => (ev: React.MouseEvent) => {
    ev.preventDefault();
    ev.stopPropagation();
    handler?.(identifier ?? '');
  };

  const fireEventWithoutPreventing = (handler?: (s: string) => void) => (ev: React.MouseEvent) => {
    handler?.(identifier ?? '');
  };

  return (
    <>
        {isViewable && <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onView)}>{t('view').toLowerCase()}</StyledDropdownListItem>}
        {isEditable && <StyledDropdownListItem  isContinuumSite={isContinuumSite}isProfile={isProfile}  onClick={fireEvent(editFn)}>{t('edit').toLowerCase()}</StyledDropdownListItem>}
        {canPrintPDF && <StyledDropdownListItem  isContinuumSite={isContinuumSite}isProfile={isProfile}  onClick={fireEvent(printPdfCard)}>{t('print_project').toLowerCase()}</StyledDropdownListItem>}
        {isRemovableFromShowcase && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onMoveFromShowcase)}>
            {t('removeFromShowcase').toLowerCase()}
          </StyledDropdownListItem>
        )}
        {canAddBookmark && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onAddBookmark)}>{t(isBookmarked ? 'bookmark_remove' : 'bookmark').toLowerCase()}</StyledDropdownListItem>
        )}
        {isPublishable && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onPublish)}>{t('publish').toLowerCase()}</StyledDropdownListItem>
        )}
        {isCoauthor && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onRejectCoauthor)}>{t('reject_coauthor').toLowerCase()}</StyledDropdownListItem>
        )}
        {isCanBeDraft && !((isOwner || isCoauthor) && project && ProjectDelegate.isRejectedProject(project)) && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onMoveDrafts)}>{t('move_drafts').toLowerCase()}</StyledDropdownListItem>
        )}
        {isModerately && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onMoveModerate)}>{t('moderate').toLowerCase()}</StyledDropdownListItem>
        )}
        {isDeletable && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onDelete)}>{t('delete').toLowerCase()}</StyledDropdownListItem>
        )}
        {isRemovableFromBook && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onRemoveFromBook)}>{t('removeFromBook').toLowerCase()}</StyledDropdownListItem>
        )}
        {onDeleteAITranslation && isProjectAITranslateRemovable && (isOwner || isSuperModer || canEditAllPrj) && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onDeleteAITranslation)}>{t('delete_translation').toLowerCase()}</StyledDropdownListItem>
        )}
        {onShowOriginals && (isSuperModer || canEditAllPrj) && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={onShowOriginals}>{t('show_original').toLowerCase()}</StyledDropdownListItem>
        )}
        {canModerSendToCompetition && !!competitionList?.length && (
          competitionList?.map((competition) => (
            <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={
              () => 
                onSendToCompetition && onSendToCompetition(
                  identifier ?? '', competition?.nominationsID)}>{`Отправить на конкурс${competition?.name}`}</StyledDropdownListItem>
          ))
        )}
        {isCompetitionModer && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onApprove)}>
            {t('competition.approve').toLowerCase()}
          </StyledDropdownListItem>
        )}
        {isCompetitionModer && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onReject)}>
            {t('competition.reject').toLowerCase()}
          </StyledDropdownListItem>
        )}
        {isReportable && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEventWithoutPreventing(onReport)}>{t('send_report').toLowerCase()}</StyledDropdownListItem>
        )}
        {(isTagModerator || canViewStatistic) && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireOpen}>{t('moder_log').toLowerCase()}</StyledDropdownListItem>
        )}
        {isCanNotParticipate && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onNotParticipate)}>{t('not_participate').toLowerCase()}</StyledDropdownListItem>
        )}
        {shouldVisibleProtectButton && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} disable={protectInprocess} onClick={fireEvent(onProtect)}>{protextButtonText.toLowerCase()}</StyledDropdownListItem>
        )}
        {canSendGift && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(() => alert(`GIFT ID: ${identifier}`))}>{t('gift')}</StyledDropdownListItem>
        )}
        {onAttachProducts && isLongreadAndTeamOwner && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onAttachProducts)}>{t('shop.attach_product')}</StyledDropdownListItem>
        )}
        {isTeamOwner && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onMakeImageForOrganization)}>
            <span style={{ lineHeight: '18px', margin: '10px 0 14px 0' }}>{t('make_image_for_teams')}</span>
          </StyledDropdownListItem>
        )}
        {canEditFolders && (
          <FoldersDropdown 
            projectId={project?.projectId || identifier || ''}
            isDraft={ProjectStatusDrafts(project)} 
            toggleTooltip={toggleTooltip} 
            orientationType={orientationType}
            isContinuumSite={isContinuumSite}
            isProfile={isProfile}
          />
        )}
        {(isOwner || isCoauthor) && project && ProjectDelegate.isRejectedProject(project) && (
          <StyledDropdownListItem 
            isContinuumSite={isContinuumSite} 
            isProfile={isProfile} 
            onClick={handleOpenRequestPublicationModal}
          >
            {t('project_edit.request_publication')}
          </StyledDropdownListItem>
        )}
        {children}
        {showRequestPublicationModal && project && (
          createPortal(
            <RequestPublicationModal 
              project={project}
              onConfirm={handleRequestPublication}
              onCancel={() => setShowRequestPublicationModal(false)}
            />,
            document.body
          )
        )}
    </>
  );
};
import React, { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProjectEvents } from '../../helpers/projectEvents';
import { createCopyLinkProjectFn } from '../../helpers/copyLink';
import { useToasts, DropdownListItem } from '@brg/ui';
import { CreateLinkTo, LinkTo } from '../../constants/linkTo';
import styled from 'styled-components';
import { useHistory, useRouteMatch } from "react-router-dom";
import { API, Designer, DesignerAPI, Endpoints, Project } from '@brg/api';
import { bProfile, ProfileRole, useProfile } from "@brg/auth";
import { ProjectDelegate } from "../../../project-delegate";
import { AppTester } from "../../../supporting";
import { protectProcessStatuses } from 'shared/helpers/protectStatus';
import { css } from 'styled-components';
import { HsePolicies } from 'shared/constants/HsePolicies';
import {ModerationType} from "../../pages/teams/const/ModerationTypes";
import { FoldersDropdown, ORIENTATION_TYPE } from '../FoldersDropdown/FoldersDropdown';
import { downloadPDF } from '../PrintProject/model/downloadPDF';
import { ProjectStatusDrafts } from 'shared/helpers/projectStatus';
import { Placement } from './types';
import { RequestPublicationModal } from 'shared/modals/RequestPublicationModal';
import { createPortal } from 'react-dom';

export interface ProjectTooltipProps {
  isViewable?: boolean;
  isPublishable?: boolean;
  isDeletable?: boolean;
  isEditable?: boolean;
  isRemovableFromShowcase?: boolean;
  isRemovableFromBook?: boolean;
  isCanBeDraft?: boolean;
  isCanBeRepost?: boolean;
  isModerately?: boolean;
  identifier?: string;
  isReportable?: boolean;
  isPartInCompetition?: boolean;
  isHiddenCancel?: boolean;
  isCopyLink?: boolean;
  isCompetitionModer?: boolean;
  isInOnDisplay?: boolean;
  projectType?: string;
  isSuperModer?: boolean;
  isTeamOwner?: boolean
  isLongreadAndTeamOwner?: boolean
  isTagModerator?: boolean;
  isTagExpert?: boolean;
  orientationType?: ORIENTATION_TYPE,
  isBookmarked?: boolean;
  isCoauthor?: boolean;
  canMakeBilingual?: boolean;
  isPresentation?: boolean;
  canModerSendToCompetition?: boolean;
  canAddBookmark?: boolean;
  competitionList?: Record<string, string[]>[];
  moderId?: string;
  approvalInfo?: {context: string, name: string};
  project?: Project;
  linkPrefix?: string;
  isCanNotParticipate?: boolean;
  isCanVisibleCompetitionList?: boolean;
  canSendGift?: boolean;
  shouldVisibleProtectButton?: boolean;
  isNominated?: boolean;
  canViewStatistic?: boolean;
  canEditFolders?: boolean;
  isProjectAITranslateRemovable?: boolean;
  toggleTooltip?: (v: any) => void;
  placement?: Placement;
  repostTools?: any;
  ref?: any
}

export interface MobileTooltipProps {
  tooltipOpen: boolean,
  toggleTooltip: (value: boolean) => void
}


export const StyledDropdownListItem = styled(DropdownListItem)<{isProfile?: boolean, isContinuumSite?: boolean}>`
position: relative;
// z-index: 1;
 ${({ isProfile }) => isProfile && css`
    background: #e7e7e7;

      &:hover {
        background: #F2F2F2;
      }`
}
 ${({ isContinuumSite }) => isContinuumSite && css`
    background: rgb(31, 31, 31);

      &:hover {
        background: rgb(44, 44, 43);
        color: #909090;
      }`
}
  
`;

export const displayProjectEditError = (project: any, toasts: any, t: any, lang: any) => {
  try {
    if (project) {
      const { contexts } = project;
      const hasContexts = !!contexts?.filter((i:string) => i !== 'places' && i !== 'hsedesigntemp')?.length; 
      const contextMap = (project.approvals || []).reduce((result: any, {
        contextService,
        isApproved,
        accountId,
      }: {
        contextService: string,
        isApproved: boolean,
        accountId: string
      }) => {
        return {
          ...result,
          [contextService]: {
            contextService,
            isApproved,
            accountId,
          }
        }
      }, {});
      const approvalsArray = Object.values(contextMap);
      //@ts-ignore
      const [blockingApproval] = approvalsArray.filter(({
        isApproved,
        contextService
      }) => isApproved && contextService !== 'places' && contextService !== 'hsedesigntemp');
      if (blockingApproval) {
        const {
          //@ts-ignore
          accountId,
          //@ts-ignore
          contextService
        } = blockingApproval;
        //@ts-ignore
        DesignerAPI.Get({
          //@ts-ignore
          idDesigner: accountId
        })
        .then((d) => d.json())
        .then((designer) => {
          return API.toLang<Designer>(designer, lang || 'en')
        })
          .then(({ name, surName }) => {
            const approvalInfo = ({
              name: `${name} ${surName}`,
              context: contextService
            });
            toasts.error(t('toasts.project_on_display', approvalInfo));
            return;
          }).catch((e) => { throw e });
      }
      else if (hasContexts) {
        toasts.error(t('toasts.project_on_display_empty'));
        return;
      }
    }
  } catch (e) {
    toasts.error(t('toasts.project_on_display', {}));
  }
}

/**
 * **ProjectTooltip** - контент всплывающего меню со всеми необходимыми параметрами и настройками для проекта.
 *
 * @param props
 */
export const ProjectTooltip: FC<React.PropsWithChildren<ProjectTooltipProps & ProjectEvents>> = (props) => {
  const { t, i18n } = useTranslation();
  const toasts = useToasts();
  const history = useHistory();
  const profile = useProfile();
  const { error } = useToasts();
  const [showRequestPublicationModal, setShowRequestPublicationModal] = useState(false);

  const {
    isViewable,
    isPublishable,
    isEditable,
    isReportable,
    isDeletable,
    isCanBeDraft,
    repostTools,
    projectType,
    // isHiddenCancel,
    isCopyLink,
    identifier,
    isModerately,
    isRemovableFromShowcase,
    isRemovableFromBook,
    isCompetitionModer,
    isTeamOwner,
    isLongreadAndTeamOwner,
    canModerSendToCompetition,
    canAddBookmark,
    competitionList,
    isInOnDisplay,
    isPartInCompetition,
    children,
    isCanNotParticipate,
    canEditFolders,
    isPresentation,
    canSendGift,
    toggleTooltip,
    onNotParticipate,
    onAddBookmark,
    onRepostRemove,
    onMoveFromShowcase,
    onRemoveFromBook,
    onPublish,
    onDelete,
    onEdit,
    onMoveDrafts,
    onRejectCoauthor,
    onSendToCompetition,
    onMoveModerate,
    onReport,
    onView,
    onApprove,
    onReject,
    onProtect,
    onMakeImageForOrganization,
    onAttachProducts,
    isTagModerator,
    orientationType,
    isCoauthor,
    isSuperModer,
    project,
    linkPrefix,
    isBookmarked,
    shouldVisibleProtectButton,
    onShowOriginals,
    onDeleteAITranslation,
    canViewStatistic
  } = props;

  // Некоторые обработчики по умолчанию
  const isPro = AppTester.isProDeziiign();
  const isContinuumSite = AppTester.isContinuumSite();
  const isProfile = !!useRouteMatch(LinkTo.designer);
  const isHSEDesign = AppTester.isHSEDesign();
  const isDeziiign = AppTester.isDeziiign();
  // @ts-ignore
  const isLMS = Boolean(project?.teams?.find((team: any) => team?.moderationType === ModerationType.LMS)?.teamId || '')
      || project?.contexts?.some(( item: string ) => ['lmsproject', 'lmshseproject'].includes(item));

  const isOwner = useMemo(() => profile?.accountId === project?.author?.accountId, [profile?.accountId, project?.author?.accountId])

  const isModer = bProfile.hasRole(profile, ProfileRole.moder);
    //@ts-ignore
  const hasRootTeam =  !!project?.rootTeamId

  const canPrintPDF = hasRootTeam && (isOwner || isModer)

  const repost = useMemo(() => (repostTools) , [repostTools])
  
  const canEditAllPrj = useMemo(
    () => (profile && profile.rolesList && Array.isArray(profile.rolesList) && 
      ((new ProfileRole(HsePolicies.edit_project)).containsIn(profile.rolesList) ||
      isLMS && (new ProfileRole(HsePolicies.lms_admin)).containsIn(profile.rolesList))
  ), [isLMS, profile]);

  const isProjectAITranslateRemovable = useMemo(() => {
    //@ts-ignore
    return project?.autoTranslateStatus === 2 || project?.autoTranslateStatus === 3
  }, [project])

  // Функция для отправки запроса на публикацию
  const handleRequestPublication = useCallback(() => {
    if (!project?.projectId) return;
    
    console.log('Отправка запроса на публикацию для проекта:', project.projectId);
    // Модалка сама отправит API запрос и покажет успешное состояние
  }, [project?.projectId]);

  // Функция для открытия модалки запроса на публикацию
  const handleOpenRequestPublicationModal = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    if (!project?.projectId) {
      return;
    }
    setShowRequestPublicationModal(true);
  }, [project?.projectId]);

  const editFn = onEdit ?? (() => {
    if (!isSuperModer && !isTagModerator && !canEditAllPrj) {

      if (isInOnDisplay && !ProjectDelegate.canEditShowcaseProjects(profile)) {
        displayProjectEditError(project, toasts, t, i18n.language);
        return;
      }
      if (isPartInCompetition) {
        toasts.error(t('toasts.project_part_competition'));
        return;
      }
    }

    //@ts-ignore
    if (isPresentation || (['fixed-ratio-large', 'fixed-ratio-small', 't-shape']?.includes(project?.projectType) && !isPro && !isLMS)) {
      if (isHSEDesign) {
        const hseProUrl = window.location.origin.includes("com") 
          ? process.env.REACT_APP_URL_HSEDESIGN_PRO_COM 
          : process.env.REACT_APP_URL_HSEDESIGN_PRO_RU;
        const baseUrl = hseProUrl?.endsWith('/') ? hseProUrl.slice(0, -1) : hseProUrl;
        window.open(`${baseUrl}/edit/${identifier}`)
      }
      else if (isDeziiign && !isHSEDesign) {
        const baseUrl = process.env.REACT_APP_URL_DEZIIIGN_PRO?.endsWith('/') 
          ? process.env.REACT_APP_URL_DEZIIIGN_PRO.slice(0, -1) 
          : process.env.REACT_APP_URL_DEZIIIGN_PRO;
        window.open(`${baseUrl}/edit/${identifier}`)
      }
      else {
        const baseUrl = process.env.REACT_APP_URL_DEZIIIGN_PRO?.endsWith('/') 
          ? process.env.REACT_APP_URL_DEZIIIGN_PRO.slice(0, -1) 
          : process.env.REACT_APP_URL_DEZIIIGN_PRO;
        window.open(`${baseUrl}/edit/${identifier}`)
      }
      return;
    }
    if (project?.isBook) {
      history.push(((linkPrefix || '') + CreateLinkTo.bookEdit(identifier ?? 'undefined')));
    } else if (isLMS && ProjectStatusDrafts(project)) {
      window.open(process.env.REACT_APP_URL_HSEDESIGN_LMS_COM + CreateLinkTo.projectEdit(identifier ?? 'undefined'))
    } else {
      history.push(((linkPrefix || '') + CreateLinkTo.projectEdit(identifier ?? 'undefined')));
    }
  });

  const protextButtonText = useMemo(() => {
    const isProcess = (project as any)?.protectedStatus as number === protectProcessStatuses.InProcess;
    const isNotStart = (project as any)?.protectedStatus as number === protectProcessStatuses.NotStarted;

    if (isProcess) {
      return t('inProgress');
    }

    return !(project as any)?.isProtected || isNotStart
      ? t('protect')
      : t('unprotect');
  }, [project, t])

  const printPDFForm = useMemo(() => ({
    orientation: true,
    size: 'A4',
    localization: i18n.language,
    projectId: project?.projectId,
    pathUrlProjectRequest: 'testPp',
    pathUrlLongreadRequest: 'testLn',
    // @ts-ignore
    isPresentation: ['fixed-ratio-large', 'fixed-ratio-small', 't-shape']?.includes(project?.projectType),
}), [project, i18n.language]);


const printPdfCard = useCallback(() => {

    toasts.info(t('print_project_toast'))

    API.request()
        .endpoint(Endpoints.geograffee)
        .url('/api/Project/PrintPdf')
        .post()
        .body(printPDFForm)
        .send()
        .then((r) => r.json())
        // @ts-ignore
        .then((r) => r?.length > 0 ? downloadPDF(r, project?.title, error, toasts.info, t) : console.error('no files'))
        .catch((e) => console.error(e))
}, [printPDFForm, t]);


  const protectInprocess = useMemo(
    () => (project as any)?.protectedStatus as number === protectProcessStatuses.InProcess, [project])

  const fireOpen = (ev: React.MouseEvent) => {
    ev.preventDefault();
    ev.stopPropagation()
    window.open((Endpoints.geograffee + "/api/Project/GetStatisticForProjest?projectId=" + identifier), '_blank')?.focus();
  };

  const fireEvent = (handler?: (s: string) => void) => (ev: React.MouseEvent) => {
    ev.preventDefault();
    ev.stopPropagation();
    handler?.(identifier ?? '');
  };

  const fireEventWithoutPreventing = (handler?: (s: string) => void) => (ev: React.MouseEvent) => {
    handler?.(identifier ?? '');
  };

  return (
    <>
        {isViewable && <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onView)}>{t('view').toLowerCase()}</StyledDropdownListItem>}
        {isEditable && <StyledDropdownListItem  isContinuumSite={isContinuumSite}isProfile={isProfile}  onClick={fireEvent(editFn)}>{t('edit').toLowerCase()}</StyledDropdownListItem>}
        {canPrintPDF && <StyledDropdownListItem  isContinuumSite={isContinuumSite}isProfile={isProfile}  onClick={fireEvent(printPdfCard)}>{t('print_project').toLowerCase()}</StyledDropdownListItem>}
        {isRemovableFromShowcase && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onMoveFromShowcase)}>
            {t('removeFromShowcase').toLowerCase()}
          </StyledDropdownListItem>
        )}
        {canAddBookmark && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onAddBookmark)}>{t(isBookmarked ? 'bookmark_remove' : 'bookmark').toLowerCase()}</StyledDropdownListItem>
        )}
        {isPublishable && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onPublish)}>{t('publish').toLowerCase()}</StyledDropdownListItem>
        )}
        {isCoauthor && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onRejectCoauthor)}>{t('reject_coauthor').toLowerCase()}</StyledDropdownListItem>
        )}
        {isCanBeDraft && !((isOwner || isCoauthor) && project && ProjectDelegate.isRejectedProject(project)) && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onMoveDrafts)}>{t('move_drafts').toLowerCase()}</StyledDropdownListItem>
        )}
        {isModerately && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onMoveModerate)}>{t('moderate').toLowerCase()}</StyledDropdownListItem>
        )}
        {isDeletable && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onDelete)}>{t('delete').toLowerCase()}</StyledDropdownListItem>
        )}
        {isRemovableFromBook && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onRemoveFromBook)}>{t('removeFromBook').toLowerCase()}</StyledDropdownListItem>
        )}
        {onDeleteAITranslation && isProjectAITranslateRemovable && (isOwner || isSuperModer || canEditAllPrj) && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onDeleteAITranslation)}>{t('delete_translation').toLowerCase()}</StyledDropdownListItem>
        )}
        {onShowOriginals && (isSuperModer || canEditAllPrj) && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={onShowOriginals}>{t('show_original').toLowerCase()}</StyledDropdownListItem>
        )}
        {canModerSendToCompetition && !!competitionList?.length && (
          competitionList?.map((competition) => (
            <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={
              () => 
                onSendToCompetition && onSendToCompetition(
                  identifier ?? '', competition?.nominationsID)}>{`Отправить на конкурс${competition?.name}`}</StyledDropdownListItem>
          ))
        )}
        {isCompetitionModer && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onApprove)}>
            {t('competition.approve').toLowerCase()}
          </StyledDropdownListItem>
        )}
        {isCompetitionModer && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onReject)}>
            {t('competition.reject').toLowerCase()}
          </StyledDropdownListItem>
        )}
        {isReportable && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEventWithoutPreventing(onReport)}>{t('send_report').toLowerCase()}</StyledDropdownListItem>
        )}
        {(isTagModerator || canViewStatistic) && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireOpen}>{t('moder_log').toLowerCase()}</StyledDropdownListItem>
        )}
        {isCanNotParticipate && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onNotParticipate)}>{t('not_participate').toLowerCase()}</StyledDropdownListItem>
        )}
        {shouldVisibleProtectButton && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} disable={protectInprocess} onClick={fireEvent(onProtect)}>{protextButtonText.toLowerCase()}</StyledDropdownListItem>
        )}
        {canSendGift && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(() => alert(`GIFT ID: ${identifier}`))}>{t('gift')}</StyledDropdownListItem>
        )}
        {onAttachProducts && isLongreadAndTeamOwner && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onAttachProducts)}>{t('shop.attach_product')}</StyledDropdownListItem>
        )}
        {isTeamOwner && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onMakeImageForOrganization)}>
            <span style={{ lineHeight: '18px', margin: '10px 0 14px 0' }}>{t('make_image_for_teams')}</span>
          </StyledDropdownListItem>
        )}
        {canEditFolders && (
          <FoldersDropdown 
            projectId={project?.projectId || identifier || ''}
            isDraft={ProjectStatusDrafts(project)} 
            toggleTooltip={toggleTooltip} 
            orientationType={orientationType}
            isContinuumSite={isContinuumSite}
            isProfile={isProfile}
          />
        )}
        {(isOwner || isCoauthor) && project && ProjectDelegate.isRejectedProject(project) && (
          <StyledDropdownListItem 
            isContinuumSite={isContinuumSite} 
            isProfile={isProfile} 
            onClick={handleOpenRequestPublicationModal}
          >
            {t('project_edit.request_publication')}
          </StyledDropdownListItem>
        )}
        {children}
        {showRequestPublicationModal && project && (
          createPortal(
            <RequestPublicationModal 
              project={project}
              onConfirm={handleRequestPublication}
              onCancel={() => setShowRequestPublicationModal(false)}
            />,
            document.body
          )
        )}
    </>
  );
};
